import { FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS } from "@/lib/firm";

export const PROBATE_FEES = {
  grant: "$3,300",
  admin: "$8,800",
} as const;

export type ProbateLocation = {
  slug: string;
  name: string;
  title: string;
  metaTitle: string;
  description: string;
  lead: string;
  paragraphs: string[];
  points: { t: string; d: string }[];
  nearby: string[];
  faqs: { q: string; a: string }[];
  postcodes?: string[];
  geo?: { lat: number; lng: number };
  localHeading?: string;
  localBody?: string[];
  localFacts?: { t: string; d: string }[];
};

export type ProbateLocalDetail = {
  postcodes: string[];
  geo: { lat: number; lng: number };
  localHeading: string;
  localBody: string[];
  localFacts: { t: string; d: string }[];
};

const FEE_FAQ = (name: string) => ({
  q: `How much does a grant of probate cost in ${name}?`,
  a: `Our fixed professional fee for a straightforward grant is ${PROBATE_FEES.grant} including GST, and full estate administration is ${PROBATE_FEES.admin} including GST. The Supreme Court filing fee and the cost of publishing the required notice are set by others and charged at cost. We put both figures in writing before you engage us, and the estate normally pays them.`,
});

const COMMON_POINTS = [
  {
    t: "We tell you if you do not need a grant",
    d: "Assets held as joint tenants pass by survivorship, and superannuation and life insurance often sit outside the estate. If the bank's threshold covers it, we will say so rather than sell you an application.",
  },
  {
    t: "Notice, filing and requisitions handled",
    d: "Notice of intention to apply published and served on the Public Trustee, the application and executor's affidavit prepared and filed in the Supreme Court of Queensland, and any registry requisitions dealt with.",
  },
  {
    t: "Property in the estate dealt with in-house",
    d: "Transmission application to put the executor on title, then the sale or transfer itself. One firm, one file — the estate is not paying a second practice to finish the job.",
  },
];

const TIMING_FAQ = {
  q: "How long does it take?",
  a: "The notice must be published and served on the Public Trustee, and the application cannot be filed until at least 14 days later. Where the material is in order a grant commonly issues six to ten weeks after we start. Collecting assets and distributing takes longer again.",
};

const NO_WILL_FAQ = {
  q: "What if there is no will?",
  a: "The application becomes one for letters of administration, and the intestacy rules decide who is entitled to apply. The steps and our fixed fee are the same; the extra work sits in identifying and evidencing the next of kin.",
};

const REMOTE_FAQ = {
  q: "Do I have to attend an office?",
  a: `No. Most executors deal with us by email and phone, and documents can be signed and witnessed locally. Our offices at ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS} are available by appointment if you would rather sit down with someone.`,
};

const DISTRIBUTION_FAQ = {
  q: "When can the estate be distributed?",
  a: "Not within six months of the date of death without accepting real risk — a family provision application can still be brought, and an executor who has already paid the money out can be left personally carrying it. We will tell you when it is safe to distribute and what to do if a claim is foreshadowed.",
};

function loc(
  slug: string,
  name: string,
  title: string,
  lead: string,
  paragraphs: string[],
  points: { t: string; d: string }[],
  nearby: string[],
  faqs: { q: string; a: string }[],
  detail?: ProbateLocalDetail,
): ProbateLocation {
  return {
    ...(detail ?? {}),
    slug,
    name,
    title,
    metaTitle: `Probate Solicitor ${name} — Fixed Fee ${PROBATE_FEES.grant}`,
    description: `Fixed-fee probate and letters of administration for ${name} executors: ${PROBATE_FEES.grant} for a grant, ${PROBATE_FEES.admin} for full estate administration, both incl. GST. Call ${FIRM.phone}.`,
    lead,
    paragraphs,
    points: [...points, ...COMMON_POINTS],
    nearby,
    faqs: [...faqs, FEE_FAQ(name), TIMING_FAQ, NO_WILL_FAQ, DISTRIBUTION_FAQ, REMOTE_FAQ],
  };
}

export const PROBATE_LOCATIONS: ProbateLocation[] = [
  loc(
    "caloundra",
    "Caloundra",
    "Probate solicitors in Caloundra",
    `Executor of a Caloundra estate? A grant of probate for ${PROBATE_FEES.grant} including GST, or the whole administration handled for ${PROBATE_FEES.admin}.`,
    [
      "Caloundra estates usually turn on one or two things: the family home held in the deceased's sole name, and a bank or share holding above the institution's threshold. Either of those means the Supreme Court of Queensland needs to confirm the executor's authority before anything can be sold or transferred.",
      "A good many Caloundra estates also involve a unit bought as a downsizer, sometimes with a body corporate levy history and sometimes with a second person living in it. That is worth sorting out early, because a beneficiary in occupation changes how and when the property can be realised.",
      "We act for executors across Caloundra, Golden Beach, Kings Beach and Currimundi from our office at " +
        FIRM_ADDRESS +
        ". Erin Wilkinson runs estate files day to day and Michael Klein oversees anything contentious.",
    ],
    [
      {
        t: "Retirement village and over-50s interests",
        d: "The exit entitlement, refurbishment obligations and resale timing are contractual, not automatic. We read the residence contract before the estate assumes a figure.",
      },
      {
        t: "Downsizer units and body corporate levies",
        d: "Levy arrears, insurance and sinking fund position are checked before a sale so the estate accounts are not reopened later.",
      },
    ],
    ["Golden Beach", "Kings Beach", "Currimundi", "Pelican Waters", "Moffat Beach", "Little Mountain"],
    [
      {
        q: "The will is with a Caloundra bank or an old firm — can you retrieve it?",
        a: "Usually yes. We write to the institution or the practice holding the safe custody packet and arrange release of the original will to the executor or to us. The Court needs the original where one exists.",
      },
    ],
    {
      postcodes: ["4551 Caloundra", "4551 Golden Beach", "4551 Kings Beach", "4551 Moffat Beach", "4551 Currimundi", "4551 Pelican Waters"],
      geo: { lat: -26.8028, lng: 153.1219 },
      localHeading: "Probate in Caloundra: how it actually runs",
      localBody: [
        "There is no probate registry at Caloundra. Every Queensland application — whether the executor lives at Kings Beach or in London — is filed electronically in the Supreme Court of Queensland at Brisbane, so using a local firm costs you nothing in time and saves you the trip.",
        "The notice of intention to apply must be published on the Queensland Courts website and served on the Public Trustee, and the application cannot be filed until at least 14 clear days later. We publish in the first few days of the file so that clock is already running while the death certificate, asset list and affidavit are being assembled.",
        "Most Caloundra estates we see turn on a single title: a house at Golden Beach or Currimundi, or a downsizer unit at Kings Beach or Bulcock Beach. Once the grant issues we lodge the transmission application with Titles Queensland to put the executor on title, then run the sale from the same file.",
      ],
      localFacts: [
        { t: "Where it is filed", d: "Supreme Court of Queensland, Brisbane registry, electronically. No Caloundra court attendance is required of you." },
        { t: "Signing locally", d: "Affidavits are sworn before a solicitor or JP. We witness at 40 Theresa Street, Golden Beach, or come to you anywhere between Caloundra and Baringa." },
        { t: "Titles and rates", d: "Transmission applications go to Titles Queensland; rates, water and land tax are adjusted with Sunshine Coast Council on the estate sale." },
        { t: "Local aged care and villages", d: "Refundable accommodation deposits and village exit entitlements at Caloundra, Pelican Waters and Little Mountain are chased as estate assets, not left to drift." },
      ],
    },
  ),
  loc(
    "golden-beach",
    "Golden Beach",
    "Probate solicitors at Golden Beach",
    `Our Sunshine Coast office is at ${FIRM_ADDRESS}. Fixed-fee grants of probate (${PROBATE_FEES.grant}) and full estate administration (${PROBATE_FEES.admin}), both including GST.`,
    [
      "Golden Beach is a long-held-property suburb. Many of the estates we act on involve a house bought decades ago, now the largest asset in the estate and often the only reason a grant is needed at all.",
      "Long ownership brings its own problems: a title still in two names when one owner died years ago, a will made in the 1990s naming an executor who has since died, or a family arrangement that was never documented. Those are fixable, but they are better identified at the start than at the point of sale.",
      "Erin Wilkinson lives in Golden Beach and handles estate files locally. If it suits you, she will come to you.",
    ],
    [
      {
        t: "Older wills and dead or unwilling executors",
        d: "Where the named executor has died, renounced or cannot act, we deal with the reserve appointment or the application for letters of administration with the will annexed.",
      },
      {
        t: "Titles never updated after an earlier death",
        d: "A survivorship or transmission application from a death years ago is often needed before the current estate can sell. We deal with both in the one file.",
      },
    ],
    ["Caloundra", "Pelican Waters", "Bells Creek", "Aroona", "Bulcock Beach", "Baringa"],
    [
      {
        q: "Can someone come to the house?",
        a: "Yes. Erin is based in Golden Beach and regularly sees executors and elderly clients at home rather than asking them to travel.",
      },
    ],
    {
      postcodes: ["4551 Golden Beach", "4551 Pelican Waters", "4551 Bulcock Beach", "4551 Aroona", "4551 Bells Creek", "4551 Baringa"],
      geo: { lat: -26.8134, lng: 153.1291 },
      localHeading: "Probate for Golden Beach executors",
      localBody: [
        "Our Sunshine Coast office is at 40 Theresa Street, Golden Beach, so this is the one suburb where you can genuinely walk in with the will. Erin Wilkinson lives here and handles the estate files.",
        "Golden Beach is a long-tenure suburb: houses bought in the 1970s and 1980s, held through one death and now being dealt with on the second. That is why so many files here start with a title search that shows a name that should have come off years ago. A survivorship or earlier transmission application is dealt with in the same file rather than treated as a separate job.",
        "The application itself is filed electronically in the Supreme Court of Queensland at Brisbane. Nothing about living at Golden Beach slows a grant down — what slows estates down is a missing original will, an out-of-date title, or a superannuation fund nobody wrote to in the first month.",
      ],
      localFacts: [
        { t: "Office you can walk into", d: "40 Theresa Street, Golden Beach. Appointments, home visits and signing on site for elderly or unwell executors." },
        { t: "Second-death estates", d: "Where the title still shows a spouse who died years ago, we deal with the earlier survivorship and the current grant together." },
        { t: "Original wills", d: "Where the original sits with a Caloundra bank or a firm that has since closed, we write for its release before filing." },
        { t: "Coastal titles", d: "Esplanade and canal-front titles carry easements, revetment obligations and council approvals we check before the estate sells." },
      ],
    },
  ),
  loc(
    "scarborough",
    "Scarborough",
    "Probate solicitors at Scarborough",
    `Our peninsula office is at ${PENINSULA_ADDRESS}. Grants of probate ${PROBATE_FEES.grant}, full estate administration ${PROBATE_FEES.admin}, both including GST.`,
    [
      "A large share of Scarborough and Redcliffe peninsula estates involve a unit or a long-held waterfront home, and a family spread between the peninsula, Brisbane and interstate. The executor is often the one who lives closest, doing it for the first time.",
      "The peninsula also has a heavy concentration of retirement village and over-50s residents, which changes what the estate is actually worth. The exit entitlement depends on the contract, the refurbishment terms and how quickly the unit resells — not on what the resident paid to move in.",
      "Michael Klein lives on the peninsula and was managing partner of GKS Law for many years, so estate work across Scarborough, Redcliffe and Margate is familiar ground.",
    ],
    [
      {
        t: "Retirement village exits",
        d: "We read the residence contract against the exit fee and refurbishment obligations, then deal with the operator so the estate knows what it will actually receive and when.",
      },
      {
        t: "Estate sales of peninsula property",
        d: "Transmission application, executor's authority on title, and the conveyance itself — handled together rather than referred out to a second firm.",
      },
    ],
    ["Redcliffe", "Woody Point", "Margate", "Newport", "Clontarf", "Kippa-Ring"],
    [
      {
        q: "The deceased was in a retirement village — is the unit part of the estate?",
        a: "Usually the estate holds a contractual right to an exit entitlement rather than a saleable freehold interest. What the estate receives, and when, depends on the residence contract and the operator's buyback and resale obligations. We read the contract before anyone budgets on a number.",
      },
    ],
    {
      postcodes: ["4020 Scarborough", "4020 Redcliffe", "4019 Margate", "4019 Woody Point"],
      geo: { lat: -27.2013, lng: 153.1088 },
      localHeading: "Probate for Scarborough and Redcliffe peninsula executors",
      localBody: [
        "Peninsula estates are usually a house or unit held in the deceased's sole name, a bank account or two, a share parcel bought decades ago and superannuation. The property is what forces the grant, and it is also what the family most often wants sold promptly.",
        "Retirement village and over-50s living is common here, and the exit entitlement under a residence contract behaves nothing like a freehold sale. We read the contract before the estate budgets on a figure.",
        "Michael Klein works from 15 Fortune Street, Scarborough, and has acted for peninsula families since 2003, including as managing partner of GKS Law.",
      ],
      localFacts: [
        { t: "Peninsula office", d: "15 Fortune Street, Scarborough, by appointment, with home and hospital visits across the peninsula." },
        { t: "Estate sales in-house", d: "Transmission application and the sale itself handled on the same file." },
        { t: "Village exits", d: "Residence contracts, exit fees and refurbishment obligations read and negotiated with the operator." },
        { t: "Old share parcels", d: "Legacy holdings and unclaimed dividends traced and transferred or sold." },
      ],
    },
  ),
  loc(
    "redcliffe",
    "Redcliffe",
    "Probate solicitors in Redcliffe",
    `Executor of a Redcliffe estate? Fixed-fee grants (${PROBATE_FEES.grant}) and full administration (${PROBATE_FEES.admin}), including GST, from our office at ${PENINSULA_ADDRESS}.`,
    [
      "Redcliffe estates typically hold a house or unit in the deceased's sole name, a couple of bank accounts, a modest share parcel and a superannuation death benefit. The house is what forces the grant; the superannuation usually does not form part of the estate at all.",
      "That last point catches families out. Where a binding death benefit nomination has lapsed or was never made, the fund trustee decides who receives the benefit, and the will has no say in it. We deal with the fund at the same time as the grant so the two do not run at different speeds.",
      "We act for peninsula executors from 15 Fortune Street, Scarborough, and by phone and email where that is easier.",
    ],
    [
      {
        t: "Superannuation death benefits",
        d: "We check the nomination, deal with the fund trustee and advise whether the benefit is estate money or the beneficiary's own — and what the tax position is either way.",
      },
      {
        t: "Small estates and bank thresholds",
        d: "Some institutions will release funds without a grant. We ask them first, in writing, before the estate spends money on an application it may not need.",
      },
    ],
    ["Scarborough", "Margate", "Woody Point", "Clontarf", "Rothwell", "Kippa-Ring"],
    [
      {
        q: "The bank says it needs probate for a small account — is that right?",
        a: "Each institution sets its own threshold, and they differ widely. We write to the bank with the death certificate and will to see whether it will release without a grant. Where the estate also holds real property in the deceased's sole name, a grant will be needed regardless.",
      },
    ],
    {
      postcodes: ["4020 Redcliffe", "4020 Scarborough", "4021 Kippa-Ring", "4019 Clontarf"],
      geo: { lat: -27.2298, lng: 153.1075 },
      localHeading: "Probate for Redcliffe executors",
      localBody: [
        "Redcliffe has one of the older populations in Moreton Bay, and most estates here are a home or unit, bank accounts, and a superannuation death benefit that the will does not control.",
        "We deal with the fund and the Supreme Court application in parallel so the family is not waiting on one before starting the other, and we tell you honestly where a grant is not needed at all.",
        "Executors deal with us from 15 Fortune Street, Scarborough, or entirely by phone and email if that suits better.",
      ],
      localFacts: [
        { t: "Local solicitor", d: "Michael Klein, admitted 2003, formerly managing partner of GKS Law on the peninsula." },
        { t: "Bank thresholds tested first", d: "We ask each institution in writing before the estate pays for an application it may not need." },
        { t: "Superannuation", d: "Nominations checked, trustee dealt with, tax position on a benefit to an adult child explained." },
        { t: "Fixed fees", d: `Grant ${PROBATE_FEES.grant}, full administration ${PROBATE_FEES.admin}, both incl. GST, in writing before you engage us.` },
      ],
    },
  ),
  loc(
    "caboolture",
    "Caboolture",
    "Probate solicitors in Caboolture",
    `Grants of probate and letters of administration for Caboolture, Morayfield and Burpengary estates — ${PROBATE_FEES.grant} for a grant, ${PROBATE_FEES.admin} for the whole administration, including GST.`,
    [
      "Caboolture and Morayfield estates often involve an acreage block, a manufactured home in a residential park, or a house that has been in the family for a long time. Each of those is handled differently once the owner has died.",
      "Manufactured home parks are the one most people get wrong. The home is owned, the land is not — what the estate holds is a site agreement plus a home to be sold or removed, and the park operator's rules govern the timing.",
      "Intestacy is more common here than people expect. Where there is no will, the entitlement is fixed by statute, and blended families produce an outcome that surprises everyone involved.",
    ],
    [
      {
        t: "Acreage and rural-residential blocks",
        d: "Water entitlements, sheds built without approval and boundary issues are worth finding before the estate lists the property, not during the contract.",
      },
      {
        t: "Manufactured home parks",
        d: "Site agreements, park rules and the operator's sale process determine what the estate can do with the home and how long it takes.",
      },
    ],
    ["Morayfield", "Burpengary", "Elimbah", "Narangba", "Wamuran", "Bribie Island"],
    [
      {
        q: "There is no will and the family is blended — who is entitled?",
        a: "Queensland's intestacy rules give the surviving spouse a statutory legacy and household chattels, then split the balance with the children, and a de facto partner of two years or more counts as a spouse. Where there are children from an earlier relationship the result is frequently not what anyone expected, and it is worth advice early.",
      },
    ],
    {
      postcodes: ["4510 Caboolture", "4506 Morayfield", "4505 Burpengary", "4504 Narangba", "4516 Elimbah", "4512 Wamuran"],
      geo: { lat: -27.0847, lng: 152.9513 },
      localHeading: "Probate for Caboolture and Morayfield estates",
      localBody: [
        "Caboolture has a Magistrates Court, but probate is not a Magistrates Court matter. Grants of probate and letters of administration are made by the Supreme Court of Queensland and filed electronically at Brisbane, so no local court attendance arises for you at any stage.",
        "The estate profile out here is different from the coast. More acreage and rural-residential blocks, more manufactured homes in residential parks, and more estates where no will was ever made. Each of those changes the paperwork rather than the fee.",
        "Where there is no will, we apply for letters of administration and evidence the next of kin under Queensland's intestacy rules. In blended families that evidence takes more work than the application itself, and it is the part that is worth getting right the first time.",
      ],
      localFacts: [
        { t: "Where it is filed", d: "Supreme Court of Queensland at Brisbane, electronically. Caboolture Magistrates Court has no probate jurisdiction." },
        { t: "Meeting you", d: "Scarborough (15 Fortune Street) is roughly 25 minutes from Caboolture; most files run entirely by phone and email." },
        { t: "Manufactured home parks", d: "The home is an estate asset, the land is not. Site agreements and park rules govern the sale timing and we deal with the operator directly." },
        { t: "Intestate estates", d: "Letters of administration, statutory legacy calculations and next-of-kin evidence for blended Moreton Bay families." },
      ],
    },
  ),
  loc(
    "bribie-island",
    "Bribie Island",
    "Probate solicitors for Bribie Island",
    `Bellara, Bongaree, Woorim and Banksia Beach estates — fixed-fee grants of probate at ${PROBATE_FEES.grant} and full estate administration at ${PROBATE_FEES.admin}, including GST.`,
    [
      "Bribie has one of the older populations in South East Queensland, and the estates reflect it: a modest house or unit, a term deposit, a share parcel bought a long time ago and often an aged care accommodation deposit still sitting with the provider.",
      "The refundable accommodation deposit is worth flagging. It is an estate asset, the provider has a statutory period in which to refund it, and it should be chased rather than waited on.",
      "We act for Bribie executors without asking them to travel — most of the file runs by phone and email, and we can meet at Caloundra when signing is needed.",
    ],
    [
      {
        t: "Aged care refundable deposits",
        d: "We claim the refundable accommodation deposit from the provider and follow the statutory refund period rather than letting it drift.",
      },
      {
        t: "Old share parcels and lost holdings",
        d: "Historic share registries, unclaimed money and dormant accounts are traced as part of administration so the estate is not distributed with assets left behind.",
      },
    ],
    ["Bellara", "Bongaree", "Woorim", "Banksia Beach", "Ningi", "Sandstone Point"],
    [
      {
        q: "Do we have to travel to a Brisbane firm?",
        a: "No. The application is filed electronically in the Supreme Court of Queensland regardless of where your solicitor sits. We run Bribie estates from Caloundra and the peninsula, and can meet on the Sunshine Coast side if signing in person suits you.",
      },
    ],
    {
      postcodes: ["4507 Bongaree", "4507 Bellara", "4507 Woorim", "4507 Banksia Beach", "4511 Ningi", "4511 Sandstone Point"],
      geo: { lat: -27.0703, lng: 153.1652 },
      localHeading: "Probate for Bribie Island executors",
      localBody: [
        "Bribie executors are usually told to go to Brisbane. There is no reason to. The application is filed electronically in the Supreme Court of Queensland wherever your solicitor sits, and we run Bribie estates from Caloundra, about 40 minutes up the road, or from Scarborough on the peninsula side.",
        "The island's demographics shape the files. A high proportion of estates here involve aged care: a refundable accommodation deposit held by the provider, a home at Bongaree or Bellara sold to fund it, and a family living somewhere else entirely. The deposit is an estate asset and the provider has a statutory period to refund it once the grant issues.",
        "The other recurring feature is old paper. Share parcels bought through a 1990s float, passbook accounts, and unclaimed money sitting with the Public Trustee or ASIC. We search for those before distribution rather than after.",
      ],
      localFacts: [
        { t: "No trip to Brisbane", d: "Filed electronically in the Supreme Court of Queensland. Signing can be witnessed by a local JP or by us at Caloundra." },
        { t: "Aged care refunds", d: "Refundable accommodation deposits claimed from the provider and followed through the statutory refund period." },
        { t: "Island titles", d: "Bongaree, Bellara, Woorim and Banksia Beach houses and units transferred to the executor by transmission, then sold from the same file." },
        { t: "Lost assets", d: "Historic share registries, dormant accounts and unclaimed money traced before the estate is distributed." },
      ],
    },
  ),
  loc(
    "north-lakes",
    "North Lakes",
    "Probate solicitors in North Lakes",
    `Fixed-fee probate for North Lakes, Mango Hill and Murrumba Downs executors — ${PROBATE_FEES.grant} for a grant, ${PROBATE_FEES.admin} for full administration, including GST.`,
    [
      "North Lakes estates skew younger than the peninsula: a mortgaged house, a superannuation balance with insurance attached, and sometimes a self-managed fund or a small company. The superannuation is often the largest single asset and the one least governed by the will.",
      "Where there is a mortgage, the estate has to keep the loan serviced or come to an arrangement with the lender while the grant is obtained. That is a conversation worth having in the first week, not the third month.",
      "Community title and covenanted estates add a layer to any sale by the executor — levies, disclosure and the body corporate's records all need to be in order before settlement.",
    ],
    [
      {
        t: "Mortgaged property in the estate",
        d: "We deal with the lender early so interest and default do not erode the estate while the grant is pending.",
      },
      {
        t: "Superannuation and insurance claims",
        d: "Binding nominations, trustee discretion and the tax treatment of a death benefit paid to an adult child are all checked before the estate assumes the money is coming.",
      },
    ],
    ["Mango Hill", "Murrumba Downs", "Griffin", "Kallangur", "Dakabin", "Rothwell"],
    [
      {
        q: "The deceased had a self-managed super fund — does that change things?",
        a: "Yes, considerably. Control of the fund passes according to the trust deed and who is appointed trustee, not according to the will, and the surviving trustee can effectively decide where the death benefit goes. It needs advice before anything is signed.",
      },
    ],
    {
      postcodes: ["4509 North Lakes", "4509 Mango Hill", "4503 Murrumba Downs", "4503 Kallangur"],
      geo: { lat: -27.2266, lng: 153.0227 },
      localHeading: "Probate for North Lakes and Mango Hill executors",
      localBody: [
        "North Lakes estates are usually younger and more leveraged than peninsula estates: a mortgaged house in a community title estate, superannuation with insurance attached, and sometimes a company or self-managed fund.",
        "The order of work matters. Lender first so the loan does not fall into default, then the superannuation and insurance claim, then the grant, then the sale or transfer of the house.",
        "We act for North Lakes, Mango Hill, Murrumba Downs and Griffin executors from our peninsula office and by phone and email, and the application is filed electronically in the Supreme Court of Queensland.",
      ],
      localFacts: [
        { t: "Mortgages first", d: "Lender contacted in the first weeks so default interest does not erode the estate." },
        { t: "Community title sales", d: "Levies, body corporate records and disclosure sorted before an executor's sale settles." },
        { t: "SMSF and company assets", d: "Trust deed, trusteeship and control reviewed before anything is signed." },
        { t: "Fixed fees", d: `Grant ${PROBATE_FEES.grant}, full administration ${PROBATE_FEES.admin}, incl. GST.` },
      ],
    },
  ),
  loc(
    "maroochydore",
    "Maroochydore",
    "Probate solicitors in Maroochydore",
    `Maroochydore, Buderim and Mooloolaba estates — grants of probate ${PROBATE_FEES.grant}, full estate administration ${PROBATE_FEES.admin}, both including GST.`,
    [
      "Sunshine Coast estates north of Caloundra often carry more than a house: an investment unit, a holiday letting arrangement, sometimes a small business or a commercial lease. Each adds a step to the administration rather than to the grant itself.",
      "Where a property is in a letting pool or on short-stay platforms, income keeps arriving after death and has to be accounted for. The estate's tax return is not optional, and the date-of-death valuation matters for the beneficiaries' future capital gains position.",
      "We are a law practice rather than a trustee company, so the fee is fixed rather than a percentage of the estate. A Buderim house does not make the paperwork harder, so it does not make the fee bigger.",
    ],
    [
      {
        t: "Investment property and letting pools",
        d: "Post-death income, agent statements and the estate tax return are dealt with properly rather than reconstructed at distribution.",
      },
      {
        t: "Date-of-death valuations",
        d: "Valuations obtained and recorded so beneficiaries have a defensible cost base when they eventually sell.",
      },
    ],
    ["Buderim", "Mooloolaba", "Alexandra Headland", "Kuluin", "Cotton Tree", "Sippy Downs"],
    [
      {
        q: "Do you charge a percentage of the estate like a trustee company?",
        a: "No. Percentage-based fees mean a larger estate pays more for identical work. We charge a fixed professional fee for the grant and a scoped fixed fee for administration, both quoted in writing before you engage us.",
      },
    ],
  ),
  loc(
    "pelican-waters",
    "Pelican Waters",
    "Probate solicitors at Pelican Waters",
    `Executor of a Pelican Waters estate? A grant of probate for ${PROBATE_FEES.grant} including GST, or the full administration handled for ${PROBATE_FEES.admin}. Our office is minutes away at ${FIRM_ADDRESS}.`,
    [
      "Pelican Waters estates tend to carry more than a house. A canal-front or golf-course home, an investment unit, sometimes a family trust or a self-managed super fund, and a share portfolio that has been held for decades.",
      "Structures change the work rather than the fee. A trust is not an estate asset; what passes is control of it, and that comes from the deed. Superannuation in a self-managed fund is governed by the trustee and the nomination, not the will. We identify all of that in the first fortnight so the executor is not distributing on a wrong assumption.",
      "Canal titles carry their own detail — revetment walls, easements and community title obligations — which matter when the executor comes to sell.",
    ],
    [
      {
        t: "Trusts, companies and SMSFs in the estate",
        d: "Deeds and constitutions read, appointor and trustee succession worked out, and the estate's actual entitlement identified before anything is distributed.",
      },
      {
        t: "Canal and waterfront property",
        d: "Transmission to the executor, then the sale, with revetment and easement obligations checked in the same file.",
      },
    ],
    ["Golden Beach", "Caloundra", "Bells Creek", "Aroona", "Baringa", "Little Mountain"],
    [
      {
        q: "The deceased controlled a family trust — is it part of the estate?",
        a: "Generally not. The trust's assets belong to the trust, and what passes is control: the appointor and trustee roles, governed by the deed and sometimes by the will if the deed allows it. It needs to be read before the estate assumes anything.",
      },
    ],
    {
      postcodes: ["4551 Pelican Waters", "4551 Bells Creek", "4551 Golden Beach", "4551 Aroona"],
      geo: { lat: -26.8322, lng: 153.1183 },
      localHeading: "Probate for Pelican Waters executors",
      localBody: [
        "We are a few minutes away at 40 Theresa Street, Golden Beach, so Pelican Waters executors can drop the original will in rather than posting it. The application itself is filed electronically in the Supreme Court of Queensland at Brisbane.",
        "The recurring feature of estates here is structure: a trust, a company, an SMSF or a second property held with someone else. Each has its own succession rule, and none of them follows the will by default. Sorting that out early is what keeps the administration on the fixed fee.",
        "Where the estate includes a canal-front home, we handle the transmission application and the sale in the same file, including the revetment and easement checks a buyer's solicitor will raise.",
      ],
      localFacts: [
        { t: "Office nearby", d: "40 Theresa Street, Golden Beach — a few minutes from Pelican Waters for signing and document handover." },
        { t: "Structures checked", d: "Trust deeds, company constitutions and SMSF deeds read before distribution." },
        { t: "Waterfront titles", d: "Revetment obligations, easements and community title interests dealt with on the estate sale." },
        { t: "Fixed fee", d: `${PROBATE_FEES.grant} for a grant and ${PROBATE_FEES.admin} for full administration — not a percentage of the estate.` },
      ],
    },
  ),
  loc(
    "currimundi",
    "Currimundi",
    "Probate solicitors at Currimundi and Little Mountain",
    `Fixed-fee grants of probate (${PROBATE_FEES.grant}) and full estate administration (${PROBATE_FEES.admin}) for Currimundi, Little Mountain, Aroona and Battery Hill executors, including GST.`,
    [
      "Currimundi and Little Mountain estates are often second-marriage estates: adult children from an earlier relationship, a surviving spouse in the house, and a will that may or may not have been updated after the relationship changed.",
      "That combination is the one most likely to attract a family provision application, and it is the reason we advise executors here not to distribute inside the six-month window without advice, however clear the will looks.",
      "Erin Wilkinson runs these files from Golden Beach, ten minutes away, and Michael Klein takes anything contentious.",
    ],
    [
      {
        t: "Blended family estates",
        d: "Where a claim is foreshadowed, we advise the executor on holding funds, notification and the six and nine-month timeframes before anything is paid out.",
      },
      {
        t: "Spouse remaining in the home",
        d: "Occupation, outgoings and the eventual sale dealt with in writing so the estate does not stall for years.",
      },
    ],
    ["Little Mountain", "Aroona", "Battery Hill", "Caloundra", "Dicky Beach", "Golden Beach"],
    [
      {
        q: "A stepchild has threatened to make a claim — what should the executor do?",
        a: "Do not distribute. An eligible person has nine months from the date of death to bring a family provision application, and an executor who has already paid the money out can be left personally exposed. Tell us early and we will deal with the notification and the timing.",
      },
    ],
    {
      postcodes: ["4551 Currimundi", "4551 Little Mountain", "4551 Aroona", "4551 Battery Hill"],
      geo: { lat: -26.7628, lng: 153.1183 },
      localHeading: "Probate for Currimundi and Little Mountain executors",
      localBody: [
        "Currimundi, Little Mountain, Aroona and Battery Hill sit ten minutes from our Golden Beach office, and most executors here prefer a local appointment to a Maroochydore or Brisbane firm. The grant itself is filed electronically in the Supreme Court of Queensland, so location changes the convenience rather than the timeline.",
        "The estates in these suburbs are typically a family home held a long time, a modest bank balance and superannuation. Where the family is blended, the difficult part is not the grant — it is knowing when it is safe to distribute.",
        "Where there is no will at all, the application becomes one for letters of administration and the intestacy rules decide who may apply. The fixed fee is the same; the extra work sits in evidencing the next of kin.",
      ],
      localFacts: [
        { t: "Ten minutes away", d: "40 Theresa Street, Golden Beach. Home and hospital visits for executors who cannot travel." },
        { t: "Distribution timing", d: "Six and nine-month family provision timeframes explained in writing before any payment out." },
        { t: "Intestate estates", d: "Letters of administration and next-of-kin evidence where no will was ever made." },
        { t: "Property in the estate", d: "Transmission application and the sale handled in the same file, with Sunshine Coast Council rates adjusted." },
      ],
    },
  ),
  loc(
    "baringa",
    "Baringa",
    "Probate solicitors at Baringa and Aura",
    `Baringa, Aura and Nirimba executors — a grant of probate for ${PROBATE_FEES.grant} including GST, or full estate administration for ${PROBATE_FEES.admin}.`,
    [
      "Baringa and Aura estates are younger than the coastal ones: a mortgaged house, superannuation with insurance attached, and often no will because nobody expected to need one.",
      "Two things matter early. The lender needs to be spoken to so the mortgage does not fall into default while the grant is pending, and the superannuation death benefit needs to be claimed properly — it is frequently the largest amount of money involved and it is not always governed by the will.",
      "We act for these families from Golden Beach, fifteen minutes away, and will come to you at Baringa if that is easier than getting to us.",
    ],
    [
      {
        t: "Mortgaged homes",
        d: "The lender is contacted early so interest and default costs do not erode the estate while the grant is obtained.",
      },
      {
        t: "Superannuation and life insurance",
        d: "Binding nominations, trustee discretion and the tax treatment of a death benefit paid to an adult child checked before the estate counts on it.",
      },
    ],
    ["Aura", "Nirimba", "Bells Creek", "Caloundra West", "Pelican Waters", "Golden Beach"],
    [
      {
        q: "There is no will — what happens?",
        a: "We apply for letters of administration instead. Queensland's intestacy rules decide who is entitled to apply and who receives the estate, which for a partner with children is a statutory legacy plus a share of the balance. The process and our fixed fee are the same.",
      },
    ],
    {
      postcodes: ["4551 Baringa", "4551 Nirimba", "4551 Bells Creek", "4551 Caloundra West"],
      geo: { lat: -26.8377, lng: 153.0489 },
      localHeading: "Probate for Baringa and Aura executors",
      localBody: [
        "Baringa and the wider Aura estate are new suburbs, so the estates we act on here rarely look like the ones a few kilometres east. A mortgage, a young family, superannuation with insurance, and often an intestacy because no will was ever made.",
        "The first fortnight matters more than usual. The lender needs to know, the superannuation fund needs to be notified and the insurance claim started, and if there is no will we need to establish who is entitled to apply for letters of administration.",
        "Our office is at 40 Theresa Street, Golden Beach, about fifteen minutes from Baringa, and the application is filed electronically in the Supreme Court of Queensland regardless of where you live.",
      ],
      localFacts: [
        { t: "Fifteen minutes away", d: "40 Theresa Street, Golden Beach, with home appointments across Baringa, Aura and Nirimba." },
        { t: "Mortgages handled early", d: "Lender contacted in the first weeks so the estate is not paying default interest." },
        { t: "Superannuation claims", d: "Death benefit and insurance claims lodged and followed, including where no binding nomination exists." },
        { t: "No will", d: "Letters of administration with next-of-kin evidence under Queensland's intestacy rules, at the same fixed fee." },
      ],
    },
  ),
  loc(
    "aura",
    "Aura",
    "Probate solicitors for Aura executors",
    `Executor of an estate at Baringa, Nirimba, Banya or Bells Creek? A grant of probate for ${PROBATE_FEES.grant} including GST, or the whole administration handled for ${PROBATE_FEES.admin}.`,
    [
      "Aura estates are unlike the coastal ones a few kilometres east. The deceased is usually younger, the house is mortgaged, the superannuation carries insurance, and more often than not there is no will — because nobody expected to need one yet.",
      "That combination changes what has to happen first. The lender needs to be told promptly so the loan does not slide into default while a grant is pending; the superannuation fund needs to be notified and any insurance claim started, because those claims are slow; and where there is no will, we need to establish who is actually entitled to apply for letters of administration under Queensland's intestacy rules before anything is filed.",
      "Superannuation is also where families most often get a nasty surprise. A death benefit paid to an adult child is taxed differently from one paid to a spouse or a financially dependent child, and if there is no binding nomination the trustee decides where it goes — which may not be where the family assumed.",
      "We act for Aura executors from our office at 40 Theresa Street, Golden Beach, about ten minutes from the estate, and will come to you if that is easier.",
    ],
    [
      {
        t: "Mortgaged homes",
        d: "The lender is contacted in the first weeks so default interest and enforcement costs do not eat into what the beneficiaries receive while the grant is obtained.",
      },
      {
        t: "Superannuation and insurance claims",
        d: "Fund notified, binding nomination checked, insurance claim lodged and followed, and the tax treatment of a benefit paid to an adult child confirmed before the estate counts on the figure.",
      },
      {
        t: "Intestacy and next of kin",
        d: "Where there is no will, we establish and evidence who is entitled to apply and who takes under the intestacy rules, at the same fixed fee as a grant of probate.",
      },
    ],
    ["Baringa", "Nirimba", "Banya", "Bells Creek", "Caloundra West", "Pelican Waters"],
    [
      {
        q: "There is no will — what happens?",
        a: "We apply for letters of administration instead. The intestacy rules in Queensland decide who may apply and who receives the estate, which for a surviving partner with children is a statutory legacy plus a share of the balance. The process and our fixed fee are the same; the additional work is in identifying and evidencing the next of kin.",
      },
      {
        q: "The house still has a mortgage on it — what do we do?",
        a: "Tell the lender early and put the account on notice that the borrower has died. Most lenders will hold enforcement while an estate is being administered, but only if they know. Whether the loan is paid out by insurance, whether the property is sold or transferred, and who carries the repayments in the meantime are the three things to settle in the first month.",
      },
      {
        q: "Do we need a grant if the house was in both names?",
        a: "Often not for the house itself. If it was held as joint tenants, it passes to the survivor by survivorship and only a survivorship application on the title is needed. If it was held as tenants in common, the deceased's share forms part of the estate and a grant is usually required. We check the title before the estate spends money on an application it may not need.",
      },
    ],
    {
      postcodes: ["4551 Baringa", "4551 Nirimba", "4551 Banya", "4551 Bells Creek", "4551 Caloundra West"],
      geo: { lat: -26.8377, lng: 153.0489 },
      localHeading: "Probate for Aura executors: what to do first",
      localBody: [
        "There is no probate registry on the Sunshine Coast. Every Queensland application is filed electronically in the Supreme Court of Queensland at Brisbane, so acting through a local firm costs you nothing in time and saves the trip.",
        "The notice of intention to apply is published on the Queensland Courts website and served on the Public Trustee, and the application cannot be filed until at least fourteen clear days later. We publish in the first few days of the file so that clock is already running while the death certificate, the asset list and the affidavit are being assembled.",
        "Most Aura estates turn on one title and one superannuation account. Once the grant issues we lodge the transmission application with Titles Queensland to put the executor on title, then run the sale or transfer from the same file rather than sending the estate to a second firm.",
      ],
      localFacts: [
        { t: "Ten minutes away", d: "40 Theresa Street, Golden Beach, with home appointments across Baringa, Nirimba, Banya and Bells Creek." },
        { t: "Where it is filed", d: "Supreme Court of Queensland, Brisbane registry, electronically. No court attendance is required of you." },
        { t: "Mortgages handled early", d: "Lender contacted in the first weeks so the estate is not paying default interest while the grant is pending." },
        { t: "No will", d: "Letters of administration with next-of-kin evidence under Queensland's intestacy rules, at the same fixed fee." },
      ],
    },
  ),
  loc(
    "margate",

    "Margate",
    "Probate solicitors at Margate and Woody Point",
    `Margate, Woody Point and Clontarf executors — fixed-fee grants of probate ${PROBATE_FEES.grant} and full estate administration ${PROBATE_FEES.admin}, both including GST.`,
    [
      "Margate estates are often a unit in a scheme along the foreshore or an older Woody Point house held for decades. Units bring a body corporate into the administration: levies keep accruing after death, insurance has to be confirmed, and the committee needs to know who the executor is.",
      "Where the deceased owned the property with someone else, the first question is always how it was held. Joint tenancy passes by survivorship and needs no grant; tenants in common does not.",
      "Michael Klein acts for Margate and Woody Point executors from 15 Fortune Street, Scarborough, five minutes down the road.",
    ],
    [
      {
        t: "Body corporate units",
        d: "Levies, insurance and scheme records dealt with so the executor's sale is not held up at the last minute by an unpaid notice.",
      },
      {
        t: "Title checked before anything else",
        d: "We search the title to confirm how the property was held before the estate spends money on an application it may not need.",
      },
    ],
    ["Woody Point", "Clontarf", "Scarborough", "Redcliffe", "Newport", "Kippa-Ring"],
    [
      {
        q: "The unit was owned jointly with my mother — do we need probate?",
        a: "If it was held as joint tenants, it passes to her by survivorship and only a survivorship application is needed on the title. If it was held as tenants in common, the deceased's share forms part of the estate and a grant will usually be required. We check the title first.",
      },
    ],
    {
      postcodes: ["4019 Margate", "4019 Woody Point", "4019 Clontarf", "4020 Scarborough"],
      geo: { lat: -27.2417, lng: 153.1069 },
      localHeading: "Probate for Margate and Woody Point executors",
      localBody: [
        "A high proportion of Margate estates involve a strata unit. That changes the practical work: the body corporate must be notified, levies continue to fall due, and the insurance position needs confirming while the unit sits empty.",
        "We handle the grant, the transmission application putting the executor on title, and the sale itself on one file, so the estate is not paying two firms to finish one job.",
        "Appointments at 15 Fortune Street, Scarborough, or at your home anywhere on the peninsula.",
      ],
      localFacts: [
        { t: "Five minutes away", d: "15 Fortune Street, Scarborough, with home and hospital visits across Margate and Woody Point." },
        { t: "Strata handled", d: "Levies, insurance and body corporate notification managed as part of the administration." },
        { t: "Title first", d: "Joint tenancy versus tenants in common confirmed before an application is prepared." },
        { t: "Fixed fees", d: `Grant ${PROBATE_FEES.grant}, full administration ${PROBATE_FEES.admin}, incl. GST.` },
      ],
    },
  ),
  loc(
    "kallangur",
    "Kallangur",
    "Probate solicitors at Kallangur and Murrumba Downs",
    `Kallangur, Murrumba Downs, Dakabin and Petrie executors — a grant of probate for ${PROBATE_FEES.grant} incl. GST, full estate administration for ${PROBATE_FEES.admin}.`,
    [
      "Estates along the Kallangur and Petrie corridor are usually modest and straightforward: a house, a bank account, superannuation, sometimes a car and a caravan. Straightforward does not mean quick if the paperwork is wrong.",
      "The most common problem we see here is the will itself — a kit will with poor witnessing, alterations in pen, or staple holes suggesting a page was removed. Each of those requires an affidavit explaining it before the registry will grant probate.",
      "We tell executors early whether a grant is needed at all, and if it is not, we say so.",
    ],
    [
      {
        t: "Problem wills",
        d: "Alterations, missing pages, staple marks and informal documents dealt with by affidavit so the registry does not requisition the application months later.",
      },
      {
        t: "Modest estates",
        d: "We test the bank's threshold in writing first. Where no grant is required, the estate keeps the money.",
      },
    ],
    ["Murrumba Downs", "Dakabin", "Petrie", "North Lakes", "Lawnton", "Griffin"],
    [
      {
        q: "The will has been written on and re-stapled — is it still valid?",
        a: "Probably, but the registry will want an affidavit explaining when the alteration was made and accounting for the staple holes. We prepare that as part of the fixed fee rather than treating it as an extra.",
      },
    ],
    {
      postcodes: ["4503 Kallangur", "4503 Murrumba Downs", "4503 Dakabin", "4502 Petrie"],
      geo: { lat: -27.2555, lng: 152.9946 },
      localHeading: "Probate for Kallangur and Petrie corridor executors",
      localBody: [
        "Most estates in this corridor are a single home, superannuation and a bank account. The grant is usually needed because the house is in the deceased's sole name, and the sale follows straight after.",
        "Kit wills and home-made alterations are common here and are the single biggest cause of delay. We deal with them by affidavit up front rather than waiting for a registry requisition.",
        "Executors work with us by phone and email, or in person at 15 Fortune Street, Scarborough.",
      ],
      localFacts: [
        { t: "Fixed fees", d: `Grant ${PROBATE_FEES.grant}, full administration ${PROBATE_FEES.admin}, incl. GST, quoted before you engage us.` },
        { t: "Kit wills handled", d: "Alterations, witnessing defects and missing pages evidenced properly the first time." },
        { t: "Grant tested first", d: "We ask the bank in writing whether it will release without a grant." },
        { t: "Sale on the same file", d: "Transmission application and conveyance handled in-house after the grant issues." },
      ],
    },
  ),
];


export function getProbateLocation(slug: string): ProbateLocation | undefined {
  return PROBATE_LOCATIONS.find((l) => l.slug === slug);
}
